import React from 'react';
import { CategoryWithChildren } from '../../crm/components/category/types';

// Generic interfaces for list item data
export interface ListItemData {
  id: number;
  displayName: string;
  subtitle?: string;
  typeName: string;
  details: ListItemDetail[];
}

export interface ListItemDetail {
  icon?: string;
  label?: string;
  value: string;
  truncated?: boolean;
}

// New unified display structure
export interface ListItemDisplayField {
  name: string;
  value: string;
  isPrimary?: boolean;
}

export interface ListItemDisplayData {
  title: string;
  category: string;
  fields: ListItemDisplayField[];
}

export interface ListItemSectionProps<T> {
  items: T[];
  selectedItemId?: number;
  onSelectItem: (item: T) => void;
  onDeleteItem: (item: T) => void;
  deleteConfirmMessage?: (item: T) => string;
  canDelete?: (item: T) => boolean; // Optional function to determine if delete button should be shown
  getInUseCount?: (item: T) => number | undefined; // Optional function to get count of associations
  loading?: boolean;
  loadingMessage?: string;
  emptyMessage?: string;
  categories?: CategoryWithChildren[];
  // New unified function to get display data
  getDisplayData: (item: T) => ListItemDisplayData;
}

// Utility functions for common formatting
export const formatCategoryName = (categoryId: number, categories: CategoryWithChildren[] = []): string => {
  if (!categoryId || categoryId === 0) {
    return 'Unknown Category';
  }
  
  // Search through all categories and their children to find the matching categoryId
  for (const parentCategory of categories) {
    // Check if it's a parent category (handle both number and string comparison)
    if (parentCategory.id === categoryId || parentCategory.id === Number(categoryId)) {
      return parentCategory.name;
    }
    // Check children categories
    if (parentCategory.children && parentCategory.children.length > 0) {
      const childCategory = parentCategory.children.find(child => 
        child.id === categoryId || child.id === Number(categoryId)
      );
      if (childCategory) {
        return childCategory.name;
      }
    }
  }
  return 'Unknown Category';
};

export const formatAddress = (address: {
  street1: string;
  street2?: string;
  unit?: string;
  city: string;
  state: string;
  zip: string;
  country?: string;
}): string => {
  const parts = [address.street1];
  
  if (address.street2) parts.push(address.street2);
  if (address.unit) parts.push(`Unit ${address.unit}`);
  
  parts.push(`${address.city}, ${address.state} ${address.zip}`);
  
  return parts.join(', ');
};

export const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  }).format(amount);
};

export const formatDate = (dateStr: string): string => {
  try {
    const [month, day, year] = dateStr.split('/');
    const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  } catch {
    return dateStr;
  }
};

export const formatPhoneNumber = (phone?: { phoneNumber: string }): string => {
  return phone ? phone.phoneNumber : 'No phone';
};

export const formatEmail = (email?: { emailAddress: string }): string => {
  return email ? email.emailAddress : 'No email';
};

export const formatContacts = (contacts: Array<{ firstName: string; lastName: string }>): string => {
  if (!contacts || contacts.length === 0) {
    return 'No contacts';
  }
  
  if (contacts.length === 1) {
    const contact = contacts[0];
    return `${contact.firstName} ${contact.lastName}`;
  }
  
  return `${contacts.length} contacts`;
};

const ListItemSection = <T extends { id: number }>({
  items,
  selectedItemId,
  onSelectItem,
  onDeleteItem,
  deleteConfirmMessage,
  canDelete,
  getInUseCount,
  loading = false,
  loadingMessage = 'Loading items...',
  emptyMessage = 'No items found. Try adjusting your search criteria.',
  getDisplayData
}: ListItemSectionProps<T>) => {
  
  const handleDelete = (item: T) => {
    const message = deleteConfirmMessage 
      ? deleteConfirmMessage(item)
      : `Are you sure you want to delete this item?`;
    
    if (window.confirm(message)) {
      onDeleteItem(item);
    }
  };

  if (loading) {
    return (
      <div className="list-items">
        <div className="app-loading">{loadingMessage}</div>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="list-items">
        <div className="list-no-items">
          {emptyMessage}
        </div>
      </div>
    );
  }

  return (
    <div className="list-items">
      {items.map((item) => {
        const displayData = getDisplayData(item);
        
        return (
          <div
            key={item.id}
            className={`list-item ${selectedItemId === item.id ? 'selected' : ''}`}
            onClick={() => onSelectItem(item)}
          >
            {/* Primary Information */}
            <div className="list-item-main-info">
              <div className="list-item-name-section">
                <div className="list-item-name">
                  {displayData.title}
                </div>
                <div className="list-item-category">
                  {displayData.category}
                </div>
              </div>
            </div>
            
            {/* Secondary Information */}
            <div className="list-item-details">
              {displayData.fields.map((field, index) => (
                <div 
                  key={index} 
                  className={`list-item-detail ${field.isPrimary ? 'primary' : ''}`}
                >
                  <span className="list-item-detail-label">{field.name}:</span>
                  <span className="list-item-detail-value">{field.value}</span>
                </div>
              ))}
            </div>
            
            {/* Action Bar */}
            <div className="form-item-actions">
              {(() => {
                const inUseCount = getInUseCount ? getInUseCount(item) : undefined;
                const canDeleteItem = canDelete === undefined || canDelete(item);
                
                if (inUseCount !== undefined && inUseCount > 0) {
                  // Show count badge instead of delete button
                  return (
                    <div className="in-use-badge" title={`Address is used by ${inUseCount} ${inUseCount === 1 ? 'association' : 'associations'}`}>
                      {inUseCount}
                    </div>
                  );
                } else if (canDeleteItem) {
                  // Show delete button
                  return (
                    <button
                      className="app-btn-delete"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(item);
                      }}
                      title="Delete item"
                    >
                    </button>
                  );
                }
                return null;
              })()}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default ListItemSection;
